import React, { useState, useEffect } from 'react';
import { FiFileText, FiCheck, FiCalendar, FiDownload, FiFilter, FiEdit, FiTrash2, FiX, FiSave, FiPlus, FiScissors, FiArrowRight } from 'react-icons/fi';
import { approveTimeEntry, getTeamMembers, getAuthToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import axios from 'axios';

// Helper to get axios config with auth token
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function Timesheet() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('week');
  const [selectedMember, setSelectedMember] = useState('all');
  const [showJobTime, setShowJobTime] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Edit modal state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    clock_in: '',
    clock_out: '',
    work_type: '',
    notes: '',
    total_break_minutes: 0
  });
  const [applyToCrew, setApplyToCrew] = useState(false);

  // Add manual entry modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    team_member_id: '',
    job_id: '',
    date: new Date().toLocaleDateString('en-CA'),
    clock_in_time: '08:00',
    clock_out_time: '17:00',
    work_type: 'Daily',
    notes: ''
  });
  const [jobs, setJobs] = useState([]);

  // Split entry modal state
  const [splittingEntry, setSplittingEntry] = useState(null);
  const [splitWorkOrders, setSplitWorkOrders] = useState([]);
  const [splitAllocations, setSplitAllocations] = useState([]);
  const [splitLoading, setSplitLoading] = useState(false);

  // Reassign job modal state
  const [reassigningEntry, setReassigningEntry] = useState(null);
  const [reassignJobSearch, setReassignJobSearch] = useState('');
  const [reassignJobId, setReassignJobId] = useState(null);

  const canManagePayroll = user?.can_manage_payroll || false;

  // Helper to check if user can edit an entry (own entries or payroll manager)
  const canEditEntry = (entry) => {
    return canManagePayroll || entry.team_member_id === user?.id;
  };

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadTeamMembers();
    loadJobs();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadEntries();
  }, [showJobTime]);

  const loadEntries = async () => {
    try {
      const entryType = showJobTime ? 'job' : 'payroll';
      const config = await getAuthConfig();
      // For job entries, non-admins should only see their own entries
      const ownOnly = showJobTime && !user?.can_manage_payroll ? '&own_only=true' : '';
      const response = await axios.get(`${API_URL}/time-entries?type=${entryType}${ownOnly}`, config);
      setEntries(response.data.time_entries || response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading time entries:', error);
      setEntries([]);
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      // Includes departed staff: reviewing or correcting someone's final timesheet is
      // exactly the moment you need them, and they're sorted last and labelled below.
      const response = await getTeamMembers(true);
      const members = response.data.team_members || response.data || [];
      members.sort((a, b) => (a.is_active === false) - (b.is_active === false)
                             || (a.name || '').localeCompare(b.name || ''));
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
      setTeamMembers([]);
    }
  };

  const loadJobs = async () => {
    try {
      const config = await getAuthConfig();
      // Picker list only — lite payload, see Job.to_list_dict.
      const response = await axios.get(`${API_URL}/jobs?fields=lite`, config);
      setJobs(response.data.jobs || response.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    }
  };

  const handleApprove = async (entryId) => {
    try {
      await approveTimeEntry(entryId, {});
      loadEntries();
    } catch (error) {
      console.error('Error approving entry:', error);
      alert(error.response?.data?.error || 'Failed to approve entry');
    }
  };

  const handleApproveAll = async (entriesToApprove) => {
    const unapproved = entriesToApprove.filter(e => !e.is_approved && e.clock_out);
    if (unapproved.length === 0) return;
    
    if (!window.confirm(`Approve all ${unapproved.length} entries?`)) return;
    
    try {
      for (const entry of unapproved) {
        await approveTimeEntry(entry.id, {});
      }
      loadEntries();
    } catch (error) {
      console.error('Error approving entries:', error);
      alert(error.response?.data?.error || 'Failed to approve some entries');
      loadEntries(); // Refresh to show which ones succeeded
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);

    // Parse UTC datetime and convert to local datetime-local format
    const parseToDateTimeLocal = (dateStr) => {
      if (!dateStr) return '';
      // Append Z if not present to ensure UTC parsing
      let timestamp = dateStr;
      if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
        timestamp = timestamp + 'Z';
      }
      const date = new Date(timestamp);
      // Format as local datetime-local value
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setEditForm({
      clock_in: parseToDateTimeLocal(entry.clock_in),
      clock_out: parseToDateTimeLocal(entry.clock_out),
      work_type: entry.work_type || '',
      notes: entry.notes || '',
      total_break_minutes: entry.total_break_minutes || 0
    });
    setApplyToCrew(false);  // Reset crew checkbox when opening edit modal
  };

  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    try {
      // Convert local datetime-local value to UTC ISO string for backend
      const formatForBackend = (dateTimeLocalValue) => {
        if (!dateTimeLocalValue) return null;
        // datetime-local format: "2024-01-15T09:30" (local time)
        // Convert to UTC ISO string
        const localDate = new Date(dateTimeLocalValue);
        return localDate.toISOString();
      };
      
      const updateData = {
        clock_in: formatForBackend(editForm.clock_in),
        clock_out: formatForBackend(editForm.clock_out),
        work_type: editForm.work_type,
        notes: editForm.notes,
        total_break_minutes: parseInt(editForm.total_break_minutes) || 0,
        apply_to_crew: applyToCrew
      };

      const config = await getAuthConfig();
      const response = await axios.put(`${API_URL}/time-entries/${editingEntry.id}`, updateData, config);

      // Show confirmation if crew was updated
      if (response.data.crew_updated && response.data.crew_updated > 1) {
        alert(response.data.message);
      }

      setEditingEntry(null);
      loadEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      alert(error.response?.data?.error || 'Failed to update entry');
    }
  };

  const handleAddEntry = async () => {
    if (!addForm.team_member_id || !addForm.date || !addForm.clock_in_time || !addForm.clock_out_time) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Create local date objects and convert to UTC ISO strings
      const clockInLocal = new Date(`${addForm.date}T${addForm.clock_in_time}`);
      const clockOutLocal = new Date(`${addForm.date}T${addForm.clock_out_time}`);

      const entryData = {
        team_member_id: parseInt(addForm.team_member_id),
        job_id: addForm.job_id ? parseInt(addForm.job_id) : null,
        clock_in: clockInLocal.toISOString(),
        clock_out: clockOutLocal.toISOString(),
        work_type: addForm.work_type,
        notes: addForm.notes || 'Manual entry'
      };

      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/manual`, entryData, config);
      
      setShowAddModal(false);
      setAddForm({
        team_member_id: '',
        job_id: '',
        date: new Date().toLocaleDateString('en-CA'),
        clock_in_time: '08:00',
        clock_out_time: '17:00',
        work_type: 'Daily',
        notes: ''
      });
      loadEntries();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert(error.response?.data?.error || 'Failed to add entry');
    }
  };

  const openAddModal = () => {
    setAddForm({
      team_member_id: '',
      job_id: '',
      date: new Date().toLocaleDateString('en-CA'),
      clock_in_time: '08:00',
      clock_out_time: '17:00',
      work_type: showJobTime ? 'Field Work' : 'Daily',
      notes: ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;

    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/time-entries/${entryId}`, config);
      loadEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert(error.response?.data?.error || 'Failed to delete entry');
    }
  };

  // Split entry functions
  const handleOpenSplit = async (entry) => {
    if (!entry.job_id) {
      alert('Can only split job time entries');
      return;
    }

    setSplittingEntry(entry);
    setSplitLoading(true);

    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${entry.job_id}/work-orders`, config);
      const workOrders = response.data.work_orders || response.data || [];
      setSplitWorkOrders(workOrders);

      // Initialize allocations with even split if multiple work orders
      if (workOrders.length > 0) {
        const totalHours = entry.total_hours || 0;
        const evenSplit = totalHours / workOrders.length;
        setSplitAllocations(
          workOrders.map(wo => ({
            work_order_id: wo.id,
            description: wo.description,
            po_number: wo.po_number,
            hours: parseFloat(evenSplit.toFixed(2)),
            selected: true
          }))
        );
      } else {
        setSplitAllocations([]);
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
      alert('Failed to load work orders for this job');
      setSplittingEntry(null);
    } finally {
      setSplitLoading(false);
    }
  };

  const handleSplitAllocationChange = (index, hours) => {
    const newAllocations = [...splitAllocations];
    newAllocations[index].hours = parseFloat(hours) || 0;
    setSplitAllocations(newAllocations);
  };

  const handleSplitSelectionChange = (index, selected) => {
    const newAllocations = [...splitAllocations];
    newAllocations[index].selected = selected;
    if (!selected) {
      newAllocations[index].hours = 0;
    }
    setSplitAllocations(newAllocations);
  };

  const redistributeSplitEvenly = () => {
    if (!splittingEntry) return;
    const selectedCount = splitAllocations.filter(a => a.selected).length;
    if (selectedCount === 0) return;

    const totalHours = splittingEntry.total_hours || 0;
    const evenSplit = totalHours / selectedCount;

    setSplitAllocations(
      splitAllocations.map(a => ({
        ...a,
        hours: a.selected ? parseFloat(evenSplit.toFixed(2)) : 0
      }))
    );
  };

  const handleSubmitSplit = async () => {
    if (!splittingEntry) return;

    const selectedAllocations = splitAllocations.filter(a => a.selected && a.hours > 0);
    if (selectedAllocations.length < 2) {
      alert('Please select at least 2 work orders with hours allocated');
      return;
    }

    const totalAllocated = selectedAllocations.reduce((sum, a) => sum + a.hours, 0);
    const originalHours = splittingEntry.total_hours || 0;

    if (Math.abs(totalAllocated - originalHours) > 0.01) {
      alert(`Total allocated hours (${totalAllocated.toFixed(2)}) must equal original hours (${originalHours.toFixed(2)})`);
      return;
    }

    setSplitLoading(true);
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${splittingEntry.id}/split`, {
        allocations: selectedAllocations.map(a => ({
          work_order_id: a.work_order_id,
          hours: a.hours
        }))
      }, config);

      setSplittingEntry(null);
      setSplitAllocations([]);
      loadEntries();
    } catch (error) {
      console.error('Error splitting entry:', error);
      alert(error.response?.data?.error || 'Failed to split entry');
    } finally {
      setSplitLoading(false);
    }
  };

  const handleReassignJob = async () => {
    if (!reassigningEntry || !reassignJobId) return;
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/time-entries/${reassigningEntry.id}`, { job_id: reassignJobId }, config);
      setReassigningEntry(null);
      setReassignJobId(null);
      setReassignJobSearch('');
      loadEntries();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to reassign entry');
    }
  };

  const getFilteredEntries = () => {
    if (!entries || !Array.isArray(entries)) return [];
    let filtered = [...entries];

    // No need to filter by job type - backend handles it based on showJobTime

    if (selectedMember !== 'all') {
      filtered = filtered.filter(e => e.team_member_id === parseInt(selectedMember));
    } else if (filter === 'mine' && user) {
      filtered = filtered.filter(e => e.team_member_id === user.id);
    }

    if (filter === 'pending') {
      filtered = filtered.filter(e => !e.is_approved && e.clock_out);
    }

    const now = new Date();
    if (dateRange === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(e => parseUTCDate(e.clock_in) >= weekAgo);
    } else if (dateRange === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(e => parseUTCDate(e.clock_in) >= monthAgo);
    }

    return filtered;
  };

  // Helper to parse UTC timestamps from backend
  const parseUTCDate = (dateStr) => {
    if (!dateStr) return null;
    // Append Z if not present to ensure UTC parsing
    let timestamp = dateStr;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
      timestamp = timestamp + 'Z';
    }
    return new Date(timestamp);
  };

  const groupEntriesByDate = (entries) => {
    const groups = {};
    entries.forEach(entry => {
      // Parse as UTC, then get local date string in YYYY-MM-DD format
      const localDate = parseUTCDate(entry.clock_in);
      const dateKey = localDate
        ? `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`
        : 'Unknown';
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });
    return groups;
  };

  const calculateTotalHours = (entries) => {
    return entries.reduce((sum, e) => sum + (e.total_hours || 0), 0).toFixed(2);
  };

  const calculatePay = (entries) => {
    return entries.reduce((sum, e) => {
      // Use stored rate from entry; fall back to member's current rate for old entries
      const rate = e.hourly_rate || (teamMembers.find(m => m.id === e.team_member_id)?.hourly_rate || 0);
      return sum + (e.total_hours || 0) * rate;
    }, 0).toFixed(2);
  };

  const exportToCSV = () => {
    const filtered = getFilteredEntries();
    const headers = ['Date', 'Employee', 'Clock In', 'Clock Out', 'Hours', 'Work Type', 'Job', 'Approved'];
    const rows = filtered.map(e => [
      parseUTCDate(e.clock_in)?.toLocaleDateString() || '',
      e.team_member_name,
      parseUTCDate(e.clock_in)?.toLocaleTimeString() || '',
      e.clock_out ? parseUTCDate(e.clock_out)?.toLocaleTimeString() : 'Active',
      e.total_hours || 0,
      e.work_type,
      e.job_number || 'Daily',
      e.is_approved ? 'Yes' : 'No'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-${showJobTime ? 'jobs' : 'payroll'}-${new Date().toLocaleDateString('en-CA')}.csv`;
    a.click();
  };

  const filteredEntries = getFilteredEntries();
  const groupedEntries = groupEntriesByDate(filteredEntries);
  const totalHours = calculateTotalHours(filteredEntries);
  const totalPay = calculatePay(filteredEntries);

  const workTypes = ['Daily', 'Field Work', 'Drafting', 'Office Work', 'Research', 'Travel', 'Review', 'Training', 'Meeting', 'Vacation (Time Off)', 'Sick (Time Off)', 'Personal (Time Off)'];

  // Get style for work type display (especially for time off types)
  const getWorkTypeStyle = (workType) => {
    if (workType?.includes('Vacation')) {
      return { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' };
    }
    if (workType?.includes('Sick')) {
      return { backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' };
    }
    if (workType?.includes('Personal')) {
      return { backgroundColor: '#f3e5f5', color: '#7b1fa2', border: '1px solid #ce93d8' };
    }
    return { backgroundColor: '#f3f4f6', color: '#374151', border: 'none' };
  };

  if (loading) {
    return <div className="loading">Loading timesheets...</div>;
  }

  return (
    <div className="timesheet-page" style={isMobile ? { padding: '10px', paddingBottom: '80px' } : {}}>
      {/* Edit Modal */}
      {editingEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: 'calc(90dvh - 90px)',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Edit Time Entry</h2>
              <button 
                onClick={() => setEditingEntry(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <p style={{ margin: '0 0 5px', color: '#666' }}>
                <strong>{editingEntry.team_member_name}</strong>
                {editingEntry.job_number && ` - ${editingEntry.job_number}`}
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Clock In</label>
              <input
                type="datetime-local"
                value={editForm.clock_in}
                onChange={(e) => setEditForm({...editForm, clock_in: e.target.value})}
                className="form-control"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Clock Out</label>
              <input
                type="datetime-local"
                value={editForm.clock_out}
                onChange={(e) => setEditForm({...editForm, clock_out: e.target.value})}
                className="form-control"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Work Type</label>
              <select
                value={editForm.work_type}
                onChange={(e) => setEditForm({...editForm, work_type: e.target.value})}
                className="form-control"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                {workTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Notes</label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                className="form-control"
                rows="3"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Break Time (minutes)</label>
              <input
                type="number"
                min="0"
                value={editForm.total_break_minutes}
                onChange={(e) => setEditForm({...editForm, total_break_minutes: e.target.value})}
                className="form-control"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                placeholder="0"
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {editForm.total_break_minutes > 0 && (
                  <span>= {(editForm.total_break_minutes / 60).toFixed(2)} hours deducted from total</span>
                )}
              </div>
            </div>

            {/* Apply to Crew checkbox - only show if entry is part of a crew */}
            {editingEntry.clock_in_group && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    checked={applyToCrew}
                    onChange={(e) => setApplyToCrew(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Apply changes to entire crew
                </label>
                <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '6px', marginLeft: '28px' }}>
                  This entry is part of a crew. Check this to update all crew members' times together.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingEntry(null)}
                className="btn btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary"
                style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <FiSave /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Manual Entry Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: 'calc(90dvh - 90px)',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Add Manual Time Entry</h2>
              <button 
                onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Employee *</label>
              <select
                value={addForm.team_member_id}
                onChange={(e) => setAddForm({...addForm, team_member_id: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                <option value="">Select employee...</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.is_active === false ? ' (no longer employed)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Job <span style={{ fontWeight: 'normal', color: '#999' }}>(leave empty for daily/payroll entry)</span>
              </label>
              <select
                value={addForm.job_id}
                onChange={(e) => setAddForm({
                  ...addForm, 
                  job_id: e.target.value,
                  work_type: e.target.value ? 'Field Work' : 'Daily'
                })}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                <option value="">No job (Daily entry)</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.id}>{j.job_number} - {j.client_name}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date *</label>
              <input
                type="date"
                value={addForm.date}
                onChange={(e) => setAddForm({...addForm, date: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Clock In *</label>
                <input
                  type="time"
                  value={addForm.clock_in_time}
                  onChange={(e) => setAddForm({...addForm, clock_in_time: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Clock Out *</label>
                <input
                  type="time"
                  value={addForm.clock_out_time}
                  onChange={(e) => setAddForm({...addForm, clock_out_time: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Work Type</label>
              <select
                value={addForm.work_type}
                onChange={(e) => setAddForm({...addForm, work_type: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              >
                {workTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Notes</label>
              <textarea
                value={addForm.notes}
                onChange={(e) => setAddForm({...addForm, notes: e.target.value})}
                rows="2"
                placeholder="Reason for manual entry..."
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>

            {/* Preview calculated hours */}
            {addForm.clock_in_time && addForm.clock_out_time && (
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '10px 15px',
                borderRadius: '6px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <span style={{ color: '#666' }}>Calculated hours: </span>
                <strong style={{ color: '#1976d2' }}>
                  {(() => {
                    const [inH, inM] = addForm.clock_in_time.split(':').map(Number);
                    const [outH, outM] = addForm.clock_out_time.split(':').map(Number);
                    const hours = (outH + outM/60) - (inH + inM/60);
                    return hours > 0 ? hours.toFixed(2) : '0.00';
                  })()}
                </strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowAddModal(false)}
                className="btn btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddEntry}
                className="btn btn-primary"
                style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <FiPlus /> Add Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Entry Modal */}
      {splittingEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: 'calc(90dvh - 90px)',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>
                <FiScissors style={{ marginRight: '8px' }} />
                Split Time Entry
              </h2>
              <button
                onClick={() => { setSplittingEntry(null); setSplitAllocations([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            {/* Entry Info */}
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{splittingEntry.team_member_name}</strong>
                {splittingEntry.job_number && (
                  <span style={{
                    marginLeft: '10px',
                    backgroundColor: '#e3f2fd',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    {splittingEntry.job_number}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Total Hours: <strong style={{ color: '#1976d2', fontSize: '16px' }}>{splittingEntry.total_hours}</strong>
              </div>
            </div>

            {splitLoading ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                Loading work orders...
              </div>
            ) : splitWorkOrders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                color: '#666',
                backgroundColor: '#fff3e0',
                borderRadius: '8px'
              }}>
                <p style={{ margin: 0 }}>No work orders found for this job.</p>
                <p style={{ margin: '10px 0 0', fontSize: '13px' }}>
                  Add work orders to the job first, then you can split time across them.
                </p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Allocate hours to work orders:</span>
                  <button
                    onClick={redistributeSplitEvenly}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#1976d2'
                    }}
                  >
                    Split Evenly
                  </button>
                </div>

                {/* Work Order Allocations */}
                <div style={{ marginBottom: '20px' }}>
                  {splitAllocations.map((alloc, index) => (
                    <div
                      key={alloc.work_order_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: alloc.selected ? '#f8f9fa' : '#fafafa',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: alloc.selected ? '1px solid #e0e0e0' : '1px solid #eee',
                        opacity: alloc.selected ? 1 : 0.6
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={alloc.selected}
                        onChange={(e) => handleSplitSelectionChange(index, e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {alloc.description}
                        </div>
                        {alloc.po_number && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            PO: {alloc.po_number}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          value={alloc.hours}
                          onChange={(e) => handleSplitAllocationChange(index, e.target.value)}
                          disabled={!alloc.selected}
                          style={{
                            width: '80px',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            textAlign: 'center',
                            fontWeight: '500'
                          }}
                        />
                        <span style={{ color: '#666', fontSize: '13px' }}>hrs</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Check */}
                {(() => {
                  const totalAllocated = splitAllocations.reduce((sum, a) => sum + (a.selected ? a.hours : 0), 0);
                  const originalHours = splittingEntry.total_hours || 0;
                  const isValid = Math.abs(totalAllocated - originalHours) < 0.01;
                  const selectedCount = splitAllocations.filter(a => a.selected && a.hours > 0).length;

                  return (
                    <div style={{
                      padding: '12px 15px',
                      borderRadius: '8px',
                      backgroundColor: isValid && selectedCount >= 2 ? '#e8f5e9' : '#fff3e0',
                      marginBottom: '20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>
                        Allocated: <strong>{totalAllocated.toFixed(2)}</strong> / {originalHours.toFixed(2)} hrs
                      </span>
                      {!isValid && (
                        <span style={{ color: '#f57c00', fontSize: '13px' }}>
                          {totalAllocated > originalHours ? 'Over-allocated' : 'Under-allocated'}
                        </span>
                      )}
                      {isValid && selectedCount < 2 && (
                        <span style={{ color: '#f57c00', fontSize: '13px' }}>
                          Select at least 2 work orders
                        </span>
                      )}
                      {isValid && selectedCount >= 2 && (
                        <span style={{ color: '#388e3c', fontSize: '13px' }}>
                          <FiCheck /> Ready to split
                        </span>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setSplittingEntry(null); setSplitAllocations([]); }}
                className="btn btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitSplit}
                disabled={splitLoading || splitWorkOrders.length < 2}
                className="btn btn-primary"
                style={{
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  opacity: (splitLoading || splitWorkOrders.length < 2) ? 0.6 : 1
                }}
              >
                <FiScissors /> Split Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Job Modal */}
      {reassigningEntry && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FiArrowRight /> Move Time Entry</h3>
              <button onClick={() => setReassigningEntry(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FiX /></button>
            </div>

            <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
              <div><strong>{reassigningEntry.team_member_name}</strong></div>
              <div>Current Job: <strong>{reassigningEntry.job_number || 'Unknown'}</strong></div>
              <div>{new Date(reassigningEntry.clock_in).toLocaleDateString()} &mdash; {((new Date(reassigningEntry.clock_out) - new Date(reassigningEntry.clock_in)) / 3600000).toFixed(2)}h</div>
            </div>

            <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Move to Job:</label>
            <input
              type="text"
              placeholder="Search jobs by number or name..."
              value={reassignJobSearch}
              onChange={(e) => { setReassignJobSearch(e.target.value); setReassignJobId(null); }}
              style={{
                width: '100%', padding: '10px', border: '1px solid #ddd',
                borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box'
              }}
            />
            {reassignJobSearch.length >= 2 && (
              <div style={{
                maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd',
                borderRadius: '6px', marginBottom: '16px'
              }}>
                {jobs
                  .filter(j => j.id !== reassigningEntry.job_id &&
                    (j.job_number?.toLowerCase().includes(reassignJobSearch.toLowerCase()) ||
                     j.client_name?.toLowerCase().includes(reassignJobSearch.toLowerCase()) ||
                     j.property_address?.toLowerCase().includes(reassignJobSearch.toLowerCase())))
                  .slice(0, 20)
                  .map(j => (
                    <div
                      key={j.id}
                      onClick={() => { setReassignJobId(j.id); setReassignJobSearch(j.job_number + (j.client_name ? ` - ${j.client_name}` : '')); }}
                      style={{
                        padding: '10px 12px', cursor: 'pointer',
                        backgroundColor: reassignJobId === j.id ? '#e3f2fd' : 'white',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px'
                      }}
                    >
                      <strong>{j.job_number}</strong>
                      {j.client_name && <span style={{ color: '#666' }}> — {j.client_name}</span>}
                      {j.property_address && <div style={{ color: '#999', fontSize: '12px' }}>{j.property_address}</div>}
                    </div>
                  ))}
                {jobs.filter(j => j.id !== reassigningEntry.job_id &&
                  (j.job_number?.toLowerCase().includes(reassignJobSearch.toLowerCase()) ||
                   j.client_name?.toLowerCase().includes(reassignJobSearch.toLowerCase()) ||
                   j.property_address?.toLowerCase().includes(reassignJobSearch.toLowerCase()))).length === 0 && (
                  <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>No matching jobs found</div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReassigningEntry(null)}
                style={{
                  padding: '10px 20px', backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReassignJob}
                disabled={!reassignJobId}
                style={{
                  padding: '10px 20px', backgroundColor: reassignJobId ? '#e65100' : '#ccc',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: reassignJobId ? 'pointer' : 'not-allowed'
                }}
              >
                <FiArrowRight style={{ marginRight: '6px' }} /> Move Entry
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header" style={isMobile ? {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '15px'
      } : {}}>
        <div>
          <h1 style={{ fontSize: isMobile ? '20px' : 'inherit' }}><FiFileText /> {isMobile ? 'Hours' : 'Timesheets'}</h1>
          {!isMobile && (
            <p className="subtitle">
              {showJobTime ? 'Job time tracking entries' : 'Daily time entries for payroll'}
            </p>
          )}
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: '10px' }}>
            {canManagePayroll && (
              <button className="btn btn-primary" onClick={openAddModal}>
                <FiPlus /> Add Entry
              </button>
            )}
            <button className="btn btn-secondary" onClick={exportToCSV}>
              <FiDownload /> Export CSV
            </button>
          </div>
        )}
      </div>

      {/* Toggle between Payroll and Job Time */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: isMobile ? '15px' : '20px',
        backgroundColor: '#e9ecef',
        borderRadius: '8px',
        padding: '4px',
        maxWidth: isMobile ? '100%' : '400px'
      }}>
        <button
          onClick={() => setShowJobTime(false)}
          style={{
            flex: 1,
            padding: isMobile ? '10px 12px' : '12px 20px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: isMobile ? '14px' : 'inherit',
            backgroundColor: !showJobTime ? '#007bff' : 'transparent',
            color: !showJobTime ? 'white' : '#333'
          }}
        >
          📋 {isMobile ? 'Payroll' : 'Payroll Hours'}
        </button>
        <button
          onClick={() => setShowJobTime(true)}
          style={{
            flex: 1,
            padding: isMobile ? '10px 12px' : '12px 20px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: isMobile ? '14px' : 'inherit',
            backgroundColor: showJobTime ? '#007bff' : 'transparent',
            color: showJobTime ? 'white' : '#333'
          }}
        >
          🔧 {isMobile ? 'Job' : 'Job Hours'}
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? '10px' : '15px',
        marginBottom: '20px',
        padding: isMobile ? '12px' : '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div className="form-group" style={{ margin: 0, minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 45%' : 'none' }}>
          <label style={{ fontSize: '12px', color: '#666' }}><FiFilter /> View</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="form-control"
            style={{ padding: '8px', fontSize: isMobile ? '14px' : 'inherit' }}
          >
            <option value="all">All Entries</option>
            <option value="mine">My Entries</option>
            <option value="pending">Pending Approval</option>
          </select>
        </div>

        <div className="form-group" style={{ margin: 0, minWidth: isMobile ? '100%' : '150px', flex: isMobile ? '1 1 45%' : 'none' }}>
          <label style={{ fontSize: '12px', color: '#666' }}><FiCalendar /> Date Range</label>
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="form-control"
            style={{ padding: '8px', fontSize: isMobile ? '14px' : 'inherit' }}
          >
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {!isMobile && (
          <div className="form-group" style={{ margin: 0, minWidth: '150px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Employee</label>
            <select 
              value={selectedMember} 
              onChange={(e) => setSelectedMember(e.target.value)}
              className="form-control"
              style={{ padding: '8px' }}
            >
              <option value="all">All Employees</option>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.is_active === false ? ' (no longer employed)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: isMobile ? '10px' : '15px',
        marginBottom: isMobile ? '15px' : '25px'
      }}>
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: isMobile ? '12px' : '20px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: 'bold', color: '#1976d2' }}>{totalHours}</div>
          <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>Total Hours</div>
        </div>
        {!showJobTime && canManagePayroll && !isMobile && (
          <div style={{
            backgroundColor: '#e8f5e9',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#388e3c' }}>${totalPay}</div>
            <div style={{ color: '#666' }}>Estimated Pay</div>
          </div>
        )}
        <div style={{
          backgroundColor: '#fff3e0',
          padding: isMobile ? '12px' : '20px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: 'bold', color: '#f57c00' }}>
            {filteredEntries.filter(e => !e.is_approved && e.clock_out).length}
          </div>
          <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>Pending</div>
        </div>
        {!isMobile && (
          <div style={{
            backgroundColor: '#f3e5f5',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#7b1fa2' }}>{filteredEntries.length}</div>
            <div style={{ color: '#666' }}>Total Entries</div>
          </div>
        )}
      </div>

      {/* Approve All Visible Button */}
      {canManagePayroll && filteredEntries.filter(e => !e.is_approved && e.clock_out).length > 0 && (
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => handleApproveAll(filteredEntries)}
            style={{
              padding: isMobile ? '10px 16px' : '12px 20px',
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FiCheck size={18} />
            Approve All Visible ({filteredEntries.filter(e => !e.is_approved && e.clock_out).length})
          </button>
        </div>
      )}

      {/* Entries grouped by date */}
      {Object.keys(groupedEntries).length > 0 ? (
        Object.entries(groupedEntries)
          .sort((a, b) => new Date(b[0]) - new Date(a[0]))
          .map(([date, dayEntries]) => (
            <div key={date} style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: isMobile ? '8px 12px' : '10px 15px',
                backgroundColor: '#e9ecef',
                borderRadius: '8px 8px 0 0',
                borderBottom: '2px solid #dee2e6'
              }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? '14px' : '16px' }}>
                  {isMobile
                    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                    : new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                  }
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {canManagePayroll && dayEntries.filter(e => !e.is_approved && e.clock_out).length > 0 && (
                    <button
                      onClick={() => handleApproveAll(dayEntries)}
                      style={{
                        padding: isMobile ? '4px 10px' : '6px 12px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '12px' : '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FiCheck size={14} />
                      Approve All ({dayEntries.filter(e => !e.is_approved && e.clock_out).length})
                    </button>
                  )}
                  <span style={{ color: '#666', fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
                    {calculateTotalHours(dayEntries)}h
                  </span>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                {dayEntries.map(entry => (
                  isMobile ? (
                    /* Mobile Entry Row - Stacked Layout */
                    <div 
                      key={entry.id} 
                      style={{
                        padding: '12px 15px',
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      {/* Top Row: Name, Hours, Status */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px'
                      }}>
                        <strong style={{ fontSize: '15px' }}>{entry.team_member_name}</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{
                              fontWeight: '600',
                              fontSize: '16px',
                              color: '#333'
                            }}>
                              {entry.total_hours ? `${entry.total_hours}h` : '—'}
                            </span>
                            {entry.total_break_minutes > 0 && (
                              <div style={{ fontSize: '10px', color: '#f57c00' }}>
                                -{entry.total_break_minutes}m break
                              </div>
                            )}
                          </div>
                          {entry.is_approved ? (
                            <span style={{
                              backgroundColor: '#e8f5e9',
                              color: '#388e3c',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              ✓
                            </span>
                          ) : (
                            <span style={{
                              backgroundColor: '#fff3e0',
                              color: '#f57c00',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              {entry.clock_out ? 'Pending' : 'Active'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Bottom Row: Time Range and Work Type */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '13px',
                        color: '#666'
                      }}>
                        <span>
                          {parseUTCDate(entry.clock_in)?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) || '—'}
                          {' → '}
                          {entry.clock_out 
                            ? parseUTCDate(entry.clock_out)?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                            : '...'
                          }
                        </span>
                        <span style={{
                          ...getWorkTypeStyle(entry.work_type),
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {entry.work_type}
                        </span>
                      </div>
                      
                      {/* Job Number (if job time view) */}
                      {showJobTime && entry.job_number && (
                        <div style={{ marginTop: '6px' }}>
                          <span style={{
                            backgroundColor: '#e3f2fd',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#1976d2'
                          }}>
                            {entry.job_number}
                          </span>
                        </div>
                      )}
                      
                      {/* Mobile Actions */}
                      {(canEditEntry(entry) || canManagePayroll) && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '10px', 
                          marginTop: '10px',
                          paddingTop: '10px',
                          borderTop: '1px solid #eee'
                        }}>
                          {canEditEntry(entry) && (
                            <button
                              onClick={() => handleEdit(entry)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <FiEdit size={14} /> Edit
                            </button>
                          )}
                          {canManagePayroll && (
                            <button
                              onClick={() => handleDelete(entry.id)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: '#fff5f5',
                                border: '1px solid #ffcdd2',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: '#f44336',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <FiTrash2 size={14} /> Delete
                            </button>
                          )}
                          {/* Split button - only for job entries */}
                          {showJobTime && entry.job_id && entry.clock_out && canEditEntry(entry) && (
                            <button
                              onClick={() => handleOpenSplit(entry)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #90caf9',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: '#1976d2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <FiScissors size={14} /> Split
                            </button>
                          )}
                          {/* Reassign button - only for job entries, admin only */}
                          {showJobTime && entry.job_id && canManagePayroll && (
                            <button
                              onClick={() => { setReassigningEntry(entry); setReassignJobId(null); setReassignJobSearch(''); }}
                              style={{
                                flex: 1,
                                padding: '8px',
                                backgroundColor: '#fff3e0',
                                border: '1px solid #ffcc80',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: '#e65100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <FiArrowRight size={14} /> Move
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Desktop Entry Row */
                    <div 
                      key={entry.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '15px',
                        borderBottom: '1px solid #eee',
                        gap: '15px'
                      }}
                    >
                      {/* Employee */}
                      <div style={{ flex: '1', minWidth: '120px' }}>
                        <strong>{entry.team_member_name}</strong>
                        <div style={{
                          fontSize: '12px',
                          marginTop: '4px',
                          display: 'inline-block'
                        }}>
                          <span style={{
                            ...getWorkTypeStyle(entry.work_type),
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            {entry.work_type}
                          </span>
                        </div>
                      </div>

                      {/* Times */}
                      <div style={{ flex: '1', minWidth: '150px' }}>
                        <div style={{ fontSize: '14px' }}>
                          {parseUTCDate(entry.clock_in)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '—'}
                          {' → '}
                          {entry.clock_out 
                            ? parseUTCDate(entry.clock_out)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : <span style={{ color: '#4caf50' }}>Active</span>
                          }
                        </div>
                      </div>

                      {/* Hours */}
                      <div style={{ width: '80px', textAlign: 'center' }}>
                        <strong style={{ fontSize: '18px' }}>{entry.total_hours || '—'}</strong>
                        <div style={{ fontSize: '11px', color: '#666' }}>hours</div>
                        {entry.total_break_minutes > 0 && (
                          <div style={{ fontSize: '10px', color: '#f57c00', marginTop: '2px' }}>
                            -{entry.total_break_minutes}m break
                          </div>
                        )}
                      </div>

                      {/* Job (only show in job time view) */}
                      {showJobTime && (
                        <div style={{ flex: '1', minWidth: '100px' }}>
                          <span style={{
                            backgroundColor: '#e3f2fd',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {entry.job_number}
                          </span>
                        </div>
                      )}

                      {/* Actions */}
                      {(canEditEntry(entry) || canManagePayroll) && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {canEditEntry(entry) && (
                            <button
                              onClick={() => handleEdit(entry)}
                              style={{
                                padding: '6px',
                                background: 'none',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Edit"
                            >
                              <FiEdit size={14} />
                            </button>
                          )}
                          {/* Split button - only for completed job entries */}
                          {showJobTime && entry.job_id && entry.clock_out && canEditEntry(entry) && (
                            <button
                              onClick={() => handleOpenSplit(entry)}
                              style={{
                                padding: '6px',
                                background: 'none',
                                border: '1px solid #90caf9',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#1976d2',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Split across work orders"
                            >
                              <FiScissors size={14} />
                            </button>
                          )}
                          {/* Reassign button - move to different job */}
                          {showJobTime && entry.job_id && canManagePayroll && (
                            <button
                              onClick={() => { setReassigningEntry(entry); setReassignJobId(null); setReassignJobSearch(''); }}
                              style={{
                                padding: '6px',
                                background: 'none',
                                border: '1px solid #ffcc80',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#e65100',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Move to different job"
                            >
                              <FiArrowRight size={14} />
                            </button>
                          )}
                          {canManagePayroll && (
                            <button
                              onClick={() => handleDelete(entry.id)}
                              style={{
                                padding: '6px',
                                background: 'none',
                                border: '1px solid #ffcdd2',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                color: '#f44336',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Delete"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Status/Actions */}
                      <div style={{ width: '120px', textAlign: 'right' }}>
                        {entry.is_approved ? (
                          <span style={{
                            backgroundColor: '#e8f5e9',
                            color: '#388e3c',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <FiCheck /> Approved
                          </span>
                        ) : entry.clock_out ? (
                          canManagePayroll ? (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => handleApprove(entry.id)}
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              Approve
                            </button>
                          ) : (
                            <span style={{
                              backgroundColor: '#fff3e0',
                              color: '#f57c00',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px'
                            }}>
                              Pending
                            </span>
                          )
                        ) : (
                          <span style={{
                            backgroundColor: '#fff3e0',
                            color: '#f57c00',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px'
                          }}>
                            In Progress
                          </span>
                        )}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          ))
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
          <FiFileText size={64} style={{ marginBottom: '15px', opacity: 0.3 }} />
          <h3>No Time Entries Found</h3>
          <p>No {showJobTime ? 'job time' : 'daily'} entries match your current filters.</p>
        </div>
      )}
    </div>
  );
}

export default Timesheet;